'use client'

import * as React from 'react'
import Link from 'next/link'
import { useRouter, usePathname } from 'next/navigation'
import { ChevronRight } from 'lucide-react'

import { MainNavItem } from '@/types'
import { siteConfig } from '@/config/site'
import { cn } from '@/lib/utils'
import { Icons } from '@/components/theme-new/icons'
import Image from 'next/image'
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet'
import { Separator } from '@/components/ui/separator'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { NavSection, NavItem } from '@/config/navigation'
import { getNavigationKey } from '@/config/navigation-key'
import { visibleSections } from '@/lib/navigation/resolve'
import { useNavSignals } from '@/lib/navigation/use-nav-signals'
import { useSchoolOptional } from '@/components/context/school-context'
import { ModeToggle } from '@/components/theme-new/mode-toggle'
import { SchoolSwitcher } from '@/app/(secure)/(core)/components/school-switcher'

interface MobileNavProps {
  items: MainNavItem[]
  children?: React.ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}

export function MobileNav({ items, children, open, onOpenChange }: MobileNavProps) {
  const router = useRouter()
  const pathname = usePathname()
  const school = useSchoolOptional()
  const { signals } = useNavSignals()

  // Which sidebar navigation to show. SHARED with app-sidebar.tsx: this used to
  // be a bare `pathname.split('/')[1]`, which missed the `/school/<section>`
  // handling and resolved /school/* to the dead legacy `school` key (SCH-1943).
  const navKey = getNavigationKey(pathname)

  // Mirror the desktop top-nav: platform-admin paths show the admin badge, every
  // other (school) path shows the school switcher — never the standalone logo.
  const isAdminPath = pathname?.startsWith('/admin') ?? false

  // Helper function to check if a menu item is active
  const isMenuItemActive = (itemUrl: string): boolean => {
    if (!pathname) return false
    return pathname === itemUrl || pathname.startsWith(`${itemUrl}/`)
  }

  // Same resolver as the desktop sidebar: gated items removed, emptied sections
  // dropped (e.g. Integrations at a school without Arbiter).
  const sidebarNavItems = React.useMemo(() => visibleSections(navKey, signals), [navKey, signals])

  const handleLinkClick = (href: string) => {
    // Close the sheet when a link is clicked
    if (onOpenChange) {
      onOpenChange(false)
    }
    // Navigate to the link
    router.push(href)
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="left" className="w-[300px] sm:w-[400px] overflow-y-auto flex flex-col">
        <SheetHeader>
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          {isAdminPath ? (
            <div className="flex items-center gap-2">
              <Image
                src="/img/logo/sj-logo-badge.png"
                alt="School Journey"
                width={28}
                height={28}
                className="rounded-full object-cover"
              />
              <span className="text-sm font-semibold">School Journey Admin</span>
            </div>
          ) : school ? (
            <div className="-ml-3 flex">
              <SchoolSwitcher />
            </div>
          ) : (
            // The marketing MainNav renders this same MobileNav without a
            // SchoolProvider (e.g. the public home page). SchoolSwitcher calls
            // the non-optional useSchool() and would throw "useSchool must be
            // used within a SchoolProvider" the moment the sheet opens. Fall
            // back to the brand logo whenever no school context is mounted.
            <div className="flex items-center gap-2">
              <Image
                src="/img/logo/sj-logo-badge.png"
                alt="School Journey"
                width={28}
                height={28}
                className="rounded-full object-cover"
              />
              <span className="text-sm font-semibold">School Journey</span>
            </div>
          )}
        </SheetHeader>

        {/* Scrollable content area */}
        <div className="flex-1 overflow-y-auto">
          {/* Top-level navigation */}
          <nav className="grid gap-1 mt-6">
            {items.map((item, index) => {
              const isActive = isMenuItemActive(item.href)
              return (
                <button
                  key={index}
                  onClick={() => handleLinkClick(item.href)}
                  disabled={item.disabled}
                  className={cn(
                    'flex w-full items-center rounded-md px-3 py-2 text-left text-sm font-medium hover:bg-accent hover:text-accent-foreground transition-colors',
                    isActive && 'bg-accent text-accent-foreground font-semibold',
                    item.disabled && 'cursor-not-allowed opacity-60'
                  )}
                >
                  {item.title}
                </button>
              )
            })}
          </nav>

          {/* Sidebar navigation (if available for current route) */}
          {sidebarNavItems.length > 0 && (
            <>
              <Separator className="my-4" />
              <div className="space-y-4">
                {sidebarNavItems.map((section: NavSection) => {
                  const sectionContent = (
                    <div key={section.title} className="space-y-1">
                      {section.separator && <Separator className="mb-4" />}

                      {section.collapse === 'none' ? (
                        <>
                          {!section.hideTitle && (
                            <h4 className="text-sm font-semibold text-muted-foreground px-3 py-2">{section.title}</h4>
                          )}
                          <div className="space-y-1">
                            {section.items.map((menuItem: NavItem) => {
                              const Icon = menuItem.icon
                              const isActive = isMenuItemActive(menuItem.url)
                              return (
                                <button
                                  key={menuItem.title}
                                  onClick={() => !menuItem.disabled && handleLinkClick(menuItem.url)}
                                  disabled={menuItem.disabled}
                                  className={cn(
                                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors',
                                    isActive && 'bg-accent text-accent-foreground font-medium',
                                    menuItem.disabled && 'cursor-not-allowed opacity-50'
                                  )}
                                >
                                  {Icon && <Icon className="h-4 w-4" />}
                                  <span>{menuItem.title}</span>
                                </button>
                              )
                            })}
                          </div>
                        </>
                      ) : (
                        <Collapsible defaultOpen={section.collapse !== 'closed'}>
                          {!section.hideTitle && (
                            <CollapsibleTrigger className="flex w-full items-center justify-between rounded-md px-3 py-2 text-sm font-semibold text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors">
                              {section.title}
                              <ChevronRight className="h-4 w-4 transition-transform duration-200 data-[state=open]:rotate-90" />
                            </CollapsibleTrigger>
                          )}
                          <CollapsibleContent className="space-y-1 mt-1">
                            {section.items.map((menuItem: NavItem) => {
                              const Icon = menuItem.icon
                              const isActive = isMenuItemActive(menuItem.url)
                              return (
                                <button
                                  key={menuItem.title}
                                  onClick={() => !menuItem.disabled && handleLinkClick(menuItem.url)}
                                  disabled={menuItem.disabled}
                                  className={cn(
                                    'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm hover:bg-accent hover:text-accent-foreground transition-colors',
                                    isActive && 'bg-accent text-accent-foreground font-medium',
                                    menuItem.disabled && 'cursor-not-allowed opacity-50',
                                    section.hideTitle ? '' : 'ml-4'
                                  )}
                                >
                                  {Icon && <Icon className="h-4 w-4" />}
                                  <span>{menuItem.title}</span>
                                </button>
                              )
                            })}
                          </CollapsibleContent>
                        </Collapsible>
                      )}
                    </div>
                  )

                  return sectionContent
                })}
              </div>
            </>
          )}

          {children}
        </div>

        {/* Mode toggle at bottom */}
        <div className="mt-auto pt-4 border-t">
          <div className="flex items-center justify-center px-3 py-2">
            <ModeToggle />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
